import type { TradeSide, TradeType, TradeSearchFilter } from "@/api/types";

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;

export const QUERY_KEYS = {
  AUTH: { user: ["auth", "user"] },
  ACCOUNT: { groups: ["account", "groups"] },
  EXCEPTIONS: {
    summary: ["exceptions", "summary"],
    list: ["exceptions", "list"],
  },
  DASHBOARD: {},
  COLUMN_CONFIG: {
    preferences: (context: string, type: string) =>
      ["column-config", "preferences", context, type] as const,
  },
  TRADE: {
    header: (sysId: number, ticketType: string) =>
      ["trade", "header", sysId, ticketType] as const,
    details: (sysId: number, ticketType: string) =>
      ["trade", "details", sysId, ticketType] as const,
    match: (sysId: number, ticketType: string) =>
      ["trade", "match", sysId, ticketType] as const,
    relatedTrades: (sysId: number, ticketType: string) =>
      ["trade", "relatedTrades", sysId, ticketType] as const,
    bdi: (sysId: number) => ["trade", "bdi", sysId] as const,
    idi: (sysId: number) => ["trade", "idi", sysId] as const,
    tba: (sysId: number, ticketType: string) =>
      ["trade", "tba", sysId, ticketType] as const,
    notes: (sysId: number, ticketType: string) =>
      ["trade", "notes", sysId, ticketType] as const,
    sent: (sysId: number, ticketType: string) =>
      ["trade", "sent", sysId, ticketType] as const,
    nack: (sysId: number, ticketType: string) =>
      ["trade", "nack", sysId, ticketType] as const,
    progress: (sysId: number, ticketType: string) =>
      ["trade", "progress", sysId, ticketType] as const,
    exceptions: (sysId: number, ticketType: string) =>
      ["trade", "exceptions", sysId, ticketType] as const,
    audit: (sysId: number, ticketType: string) =>
      ["trade", "audit", sysId, ticketType] as const,
    problems: (sysId: number, ticketType: string) =>
      ["trade", "problems", sysId, ticketType] as const,
    actions: (wiid: number, groupId: number) =>
      ["trade", "actions", wiid, groupId] as const,
    factorUpdateForm: (sysId: number) =>
      ["trade", "factorUpdateForm", sysId] as const,
    fxInstructionForm: (sysId: number) =>
      ["trade", "fxInstructionForm", sysId] as const,
    specialInstrForm: (sysId: number) =>
      ["trade", "specialInstrForm", sysId] as const,
    regenerationForm: (sysId: number) =>
      ["trade", "regenerationForm", sysId] as const,
    idiRepairForm: (sysId: number, ticketType: string) =>
      ["trade", "idiRepairForm", sysId, ticketType] as const,
    pairOffReview: (sysId: number, ticketType: string) =>
      ["trade", "pairOffReview", sysId, ticketType] as const,
    proposedMatch: (sysId: number, ticketType: string) =>
      ["trade", "proposedMatch", sysId, ticketType] as const,
    partialRegenerationForm: (sysId: number) =>
      ["trade", "partialRegenerationForm", sysId] as const,
    searchCriteria: (type: TradeType, side: TradeSide) =>
      ["trade", "searchCriteria", type, side] as const,
    searchResultsBase: ["trade", "searchResults"] as const,
    searchResults: (
      groupId: number | undefined,
      type: TradeType | undefined,
      side: TradeSide | undefined,
      filters: TradeSearchFilter[] | undefined,
    ) => ["trade", "searchResults", groupId, type, side, filters] as const,
  },
  STATIC_DATA_DETAILS: {
    base: ["static-data-details"] as const,
    header: (sysId: number, ticketType: string) =>
      ["static-data-details", "header", sysId, ticketType] as const,
    audit: (sysId: number, ticketType: string) =>
      ["static-data-details", "audit", sysId, ticketType] as const,
    actions: (ticketType: string, sysId: number) =>
      ["static-data-details", "actions", ticketType, sysId] as const,
    details: (sysId: number, ticketType: string) =>
      ["static-data-details", "details", sysId, ticketType] as const,
  },
  STATIC_ENTRY_FORM: {
    add: (type: string) => ["static-entry-form", "add", type] as const,
    edit: (type: string, id: string) =>
      ["static-entry-form", "edit", type, id] as const,
    actions: ["static-entry-form", "actions"] as const,
  },
  STATIC_DATA_LOOKUP: {
    searchResultsBase: ["static-data-lookup", "searchResults"] as const,
    menu: ["static-data-lookup", "menu"],
    criteria: (type: string) =>
      ["static-data-lookup", "criteria", type] as const,
    searchResults: (
      groupId: number | undefined,
      type: string | undefined,
      filters: TradeSearchFilter[] | undefined,
    ) =>
      ["static-data-lookup", "searchResults", groupId, type, filters] as const,
  },
} as const;

/**
 * Ticket types that get bespoke static-entry form layouts. Any other type
 * falls back to the generic metadata-driven form on the details tab.
 */
export const CUSTOM_STATIC_ENTRY_TICKET_TYPES = [
  "bs",
  "ml",
  "xv",
  "tl",
  "rl",
  "rs",
] as const;

export const STALE_TIME = {
  SHORT: 30_000, // 30 seconds
  MEDIUM: 5 * 60_000, // 5 minutes
  LONG: 30 * 60_000, // 30 minutes
} as const;

export const REFETCH_INTERVAL = {
  FAST: 10_000, // 10 seconds
} as const;

export const LOCAL_STORAGE_KEYS = {
  THEME: "vision-theme",
} as const;

export const ROUTES = {
  HOME: "/",
  LOGIN: "/login",
  EXCEPTIONS: "/exceptions",
  STATIC_DATA_LOOKUP: "/static-data-lookup",
  REPORTS: "/reports",
  TRADE_LOOKUP: "/trade-lookup",
  TRADE_DETAILS: "/trade-details",
  STATIC_DATA_DETAILS: "/static-data-details",
  STATIC_ENTRY_ADD: "/static-entry/add/$type",
  STATIC_ENTRY_EDIT: "/static-entry/edit/$type/$id",
  PAIR_OFF_REVIEW: "/pair-off-review",
  PROPOSED_MATCHING: "/proposed-matching",
  ABOUT: "/about",
} as const;
